import { PlainConditionType } from '@atoms/PlainConditionType';
import { GemSpec } from '@common/types';
import { Properties } from '@common/types/specTypes';
import { SEVERITY_TYPES } from '@LSP/constants';

import { OAuthProvider } from '../DatabricksOAuth/types';
import { Secret } from '../OrchestrationSecrets/types';
import { GenericOrchestrationConnection, ORCHESTRATION_PROVIDER } from '../types/orchestration';

/**
 * Represents the depth of the data-source hierarchy a connection exposes.
 * Backend connection specs report a numeric `hierarchyLevels` value;
 * this enum gives those numbers semantic meaning so switch-cases and
 * comparisons read clearly instead of relying on magic numbers.
 */
export enum HierarchyLevel {
  Table = 1,
  SchemaTable = 2,
  DBSchemaTable = 3
}

export enum OrchestrationConnectionCategory {
  'table' = 'table',
  'file' = 'file',
  'misc' = 'misc'
}

export enum OrchestrationConnectionType {
  SFTP = 'sftp',
  Databricks = ORCHESTRATION_PROVIDER.Databricks,
  Snowflake = ORCHESTRATION_PROVIDER.Snowflake,
  Sharepoint = 'sharepoint',
  Microsoft = 'microsoft',
  Tableau = 'tableau',
  MSSQL = 'mssql',
  S3 = 's3',
  Smartsheet = 'smartsheet',
  Mongodb = 'mongodb',
  SMTP = 'smtp',
  Oracle = 'oracle',
  Redshift = 'redshift',
  OneDrive = 'onedrive',
  BigQuery = ORCHESTRATION_PROVIDER.BigQuery,
  Hana = 'hana',
  Salesforce = 'salesforce',
  Synapse = 'synapse',
  PowerBI = 'powerbi',
  GCS = 'GCS',
  ProphecyManaged = 'ProphecyManaged',
  Minion = 'minion',
  ADLS = 'ADLS',
  PostgreSQL = 'postgres',
  Airflow = 'airflow'
}

export type OrchestrationConnection = GenericOrchestrationConnection<
  OrchestrationConnectionType,
  Record<string, unknown> & { authType?: 'oauth' | 'pat'; token?: Secret }
>;

export type DetailedOrchestrationConnectionInfo = OrchestrationConnection & {
  isExpired: boolean;
};

export type SupportedProviders = Record<OAuthProvider, string[]>;

export type ConnectionSpec = Omit<GemSpec, 'category' | 'kind'> & {
  name: string;
  kind: OrchestrationConnectionType;
  category: OrchestrationConnectionCategory;
  hierarchyLevels?: HierarchyLevel;
  defaultState: Pick<GemSpec, 'defaultState'> & {
    properties: Partial<OrchestrationConnection['properties']>;
  } & {
    metadata?: {
      supportedProviders?: SupportedProviders;
    };
  };
  supportedDataSources?: OrchestrationConnectionCategory[];
  oAuthProperties?: {
    properties: Properties;
    conditions?: Array<PlainConditionType> | PlainConditionType;
  };
  // TODO: Remove the key after toolTip key is updated to tooltip in the BE
  // as gemspecs uses tooltip key for the tooltip
  toolTip?: string;
};

export type DerivedConnectionSpec = ConnectionSpec & {
  isCreationDisabled?: boolean;
};

export type ConnectionDiagnosticsResponse = {
  diagnostics: { message: string; path: string; severity: SEVERITY_TYPES }[];
};
